import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { LogoLockup } from "./Logo";
import { useLang } from "../i18n";

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [secOpen, setSecOpen] = useState(false);
  const [hoverSec, setHoverSec] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Light theme — the white Tourism & Hospitality page. The navbar turns white
  // (dark text) once a solid background sits beneath it.
  const light =
    location.pathname === "/sectors/tourism" ||
    location.pathname === "/sectors/finance" ||
    location.pathname === "/sectors/energy";
  const solidBg = scrolled || open;
  // Meliora / Residential — the navbar turns warm brown (white text) once solid.
  const residential = location.pathname === "/sectors/residential";
  const onLight = light && solidBg;
  // Per-sector dropdown branding while the navbar sits transparent over a coloured hero —
  // the generic black panel clashes there (Corsa burgundy on Finance, deep green on Energy).
  const finance = location.pathname === "/sectors/finance";
  const energy = location.pathname === "/sectors/energy";
  // Taurus / Commercial — the navbar stays anthracite grey across the whole page.
  const commercial = location.pathname === "/sectors/commercial";
  // Retail / Liquor Store — the navbar stays dark tobacco brown across the whole page.
  const retail = location.pathname === "/sectors/retail";
  const linkCls = onLight ? "text-ink/70 hover:text-ink" : "text-white/70 hover:text-white";
  const ddPanel = residential
    ? "border border-[#6f5744]/60 bg-[#3a2a22]/95"
    : onLight
    ? "border border-ink/10 bg-white"
    : finance
    ? "border border-[#f5d27a]/25 bg-[#481512]/95"
    : energy
    ? "border border-emerald-300/20 bg-[#04210f]/95"
    : commercial
    ? "border border-white/12 bg-[#202327]"
    : retail
    ? "border border-gold/20 bg-tobacco-deep/95"
    : "border border-gold/15 bg-ink/95";
  const ddItem = residential
    ? "text-white/75 hover:bg-[#6f5744]/40 hover:text-[#e7cf9a]"
    : onLight
    ? "text-ink/70 hover:bg-[#b5952f]/10 hover:text-[#a9842f]"
    : finance
    ? "text-[#faf8f5]/75 hover:bg-[#d94b3d]/20 hover:text-[#f5d27a]"
    : energy
    ? "text-white/75 hover:bg-emerald-400/15 hover:text-emerald-200"
    : commercial
    ? "text-white/70 hover:bg-white/10 hover:text-white"
    : "text-white/65 hover:bg-gold/10 hover:text-gold-leaf";
  const ddSoon = residential ? "text-white/30" : onLight ? "text-ink/30" : finance ? "text-[#faf8f5]/35" : energy ? "text-white/35" : "text-white/30";

  const links = [
    { id: "group", label: t.nav.about },
    { id: "sectors", label: t.nav.sectors },
    { id: "portfolio", label: t.nav.portfolio },
    { id: "legacy", label: t.nav.history },
    { id: "vision", label: t.nav.vision },
  ];

  // Match the homepage Sectors order (same as the bento there).
  const SECTOR_ORDER = ["residential", "commercial", "finance", "tourism", "energy", "retail", "farm"];
  const sectors = SECTOR_ORDER.map((k) => t.sectors.items.find((s) => s.k === k)).filter(Boolean);

  const go = (id) => {
    setOpen(false);
    setSecOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 90);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Only sectors with a real page are navigable; the rest stay inert until
  // their content is ready. Add their slug to READY to enable them.
  const READY = ["residential", "commercial", "finance", "retail", "energy", "tourism"];
  const openSector = (k) => {
    if (!READY.includes(k)) return;
    setOpen(false);
    setSecOpen(false);
    navigate(`/sectors/${k}`);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.2, duration: 0.8 }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        commercial
          ? "border-b border-white/10 bg-[#1b1d20]/95 py-3 backdrop-blur-xl"
          : retail
          ? "border-b border-gold/15 bg-tobacco-deep/95 py-3 backdrop-blur-xl"
          : residential
          ? "border-b border-[#6f5744]/70 bg-[#47352e]/95 py-3 backdrop-blur-xl"
          : finance
          ? solidBg
            ? "border-b border-ink/10 bg-white/95 py-3 shadow-[0_8px_30px_rgba(0,0,0,0.06)] backdrop-blur-xl"
            : "border-b border-[#7a2a20]/50 bg-[#5a1a14]/95 py-3 backdrop-blur-xl"
          : solidBg
          ? light
            ? "border-b border-ink/10 bg-white/95 py-3 shadow-[0_8px_30px_rgba(0,0,0,0.06)] backdrop-blur-xl"
            : "border-b border-gold/15 bg-ink/95 py-3 backdrop-blur-xl"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="container-luxe flex items-center justify-between">
        <button
          data-testid="logo-home-btn"
          onClick={() => {
            setOpen(false);
            setSecOpen(false);
            if (location.pathname !== "/") {
              // From a sector (or any non-home) page, return to the homepage Sectors
              // section instead of dumping the user at the very top of the page.
              navigate("/");
              setTimeout(() => document.getElementById("sectors")?.scrollIntoView({ behavior: "smooth" }), 90);
            } else {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        >
          <LogoLockup markSize={scrolled ? 36 : 44} />
        </button>

        <div className="hidden items-center gap-9 lg:flex">
          {links.map((l) =>
            l.id === "sectors" ? (
              <div
                key="sectors"
                className="relative"
                onMouseEnter={() => setHoverSec(true)}
                onMouseLeave={() => setHoverSec(false)}
              >
                <button
                  data-testid="nav-sectors"
                  onClick={() => go("sectors")}
                  className={`group flex items-center gap-1.5 text-sm uppercase tracking-[0.18em] transition-colors ${linkCls}`}
                >
                  {l.label}
                  <ChevronDown
                    size={13}
                    className={`transition-transform duration-300 ${hoverSec ? "rotate-180" : ""}`}
                  />
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
                </button>

                {/* dropdown */}
                <div
                  className={`absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-4 transition-all duration-200 ${
                    hoverSec ? "visible opacity-100" : "invisible opacity-0"
                  }`}
                >
                  <div
                    className={`p-2 shadow-[0_12px_40px_rgba(0,0,0,0.25)] backdrop-blur-xl ${ddPanel}`}
                  >
                    {sectors.map((s) =>
                      s.soon ? (
                        <span
                          key={s.k}
                          className={`flex w-full cursor-default items-center justify-between gap-3 px-4 py-2.5 text-left text-xs uppercase tracking-[0.16em] ${ddSoon}`}
                        >
                          {s.t}
                          <span className="text-[8px] tracking-[0.2em] text-gold/40">{t.sectors.soon}</span>
                        </span>
                      ) : (
                        <button
                          key={s.k}
                          onClick={() => openSector(s.k)}
                          className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-xs uppercase tracking-[0.16em] transition-colors ${ddItem}`}
                        >
                          {s.t}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => go(l.id)}
                className={`group relative text-sm uppercase tracking-[0.18em] transition-colors ${linkCls}`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </button>
            )
          )}
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden items-center gap-1 text-xs tracking-widest sm:flex">
            <button
              data-testid="lang-switch-en"
              onClick={() => setLang("en")}
              className={lang === "en" ? "text-gold" : onLight ? "text-ink/40 hover:text-ink" : "text-white/40 hover:text-white"}
            >
              EN
            </button>
            <span className={onLight ? "text-ink/20" : "text-white/20"}>/</span>
            <button
              data-testid="lang-switch-ro"
              onClick={() => setLang("ro")}
              className={lang === "ro" ? "text-gold" : onLight ? "text-ink/40 hover:text-ink" : "text-white/40 hover:text-white"}
            >
              RO
            </button>
          </div>

          <button
            data-testid="nav-contact-btn"
            onClick={() => go("contact")}
            className="hidden border border-gold/50 px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink md:inline-block"
          >
            {t.nav.contact}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden ${onLight ? "text-ink" : "text-white"}`}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="container-luxe overflow-hidden lg:hidden"
        >
          <div className={`mt-5 flex flex-col gap-1 border-t pt-5 ${onLight ? "border-ink/10" : "border-gold/15"}`}>
            {links.map((l) =>
              l.id === "sectors" ? (
                <div key="sectors">
                  <button
                    data-testid="mobile-nav-sectors"
                    onClick={() => setSecOpen((v) => !v)}
                    className={`flex w-full items-center justify-between py-2 text-left text-sm uppercase tracking-[0.18em] ${onLight ? "text-ink/70" : "text-white/70"}`}
                  >
                    {l.label}
                    <ChevronDown
                      size={16}
                      className={`text-gold/70 transition-transform duration-300 ${secOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {secOpen && (
                    <div className={`mb-1 ml-3 flex flex-col border-l pl-4 ${onLight ? "border-ink/10" : "border-gold/15"}`}>
                      {sectors.map((s) =>
                        s.soon ? (
                          <span
                            key={s.k}
                            className={`flex items-center gap-3 py-2 text-left text-xs uppercase tracking-[0.16em] ${onLight ? "text-ink/30" : "text-white/30"}`}
                          >
                            {s.t}
                            <span className="text-[8px] tracking-[0.2em] text-gold/40">{t.sectors.soon}</span>
                          </span>
                        ) : (
                          <button
                            key={s.k}
                            onClick={() => openSector(s.k)}
                            className={`py-2 text-left text-xs uppercase tracking-[0.16em] transition-colors ${onLight ? "text-ink/60 hover:text-[#a9842f]" : "text-white/55 hover:text-gold-leaf"}`}
                          >
                            {s.t}
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => go(l.id)}
                  className={`py-2 text-left text-sm uppercase tracking-[0.18em] ${onLight ? "text-ink/70" : "text-white/70"}`}
                >
                  {l.label}
                </button>
              )
            )}

            <div className="flex items-center gap-3 pt-3">
              <button onClick={() => setLang("en")} className={lang === "en" ? "text-gold" : onLight ? "text-ink/40" : "text-white/40"}>EN</button>
              <span className={onLight ? "text-ink/20" : "text-white/20"}>/</span>
              <button onClick={() => setLang("ro")} className={lang === "ro" ? "text-gold" : onLight ? "text-ink/40" : "text-white/40"}>RO</button>
            </div>
            <button
              data-testid="mobile-contact-btn"
              onClick={() => go("contact")}
              className="mt-2 border border-gold/50 px-5 py-3 text-xs uppercase tracking-[0.2em] text-gold"
            >
              {t.nav.contact}
            </button>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
